import React, { CSSProperties, FunctionComponent, useState } from "react";
import { ListItemIcon, Menu, MenuItem, useTheme } from "@mui/material";
import { strings } from "../../../localization/LocalizedStrings";
import { useContextMenu } from "../../../hooks/contextMenu";
import { ToolbarRawButton } from "../../toolbar/ToolbarRawButton";
import styles from "./styles.module.scss";
import { IShelfSegment } from "../../../hooks/shelfSegments";
import { StatusCodesHelper } from "../../../models/StatusCodesHelper";
import { toast } from "react-toastify";
import { IShelving } from "../../../hooks/shelvings";
import { AlertDialog } from "../../AlertDialog";
import { useRemoveAllAlbumsFromShelfSegment } from "../../../hooks/shelfSegments/useRemoveAllAlbumsFromShelfSegment";
import { IShelf } from "../../../hooks/shelves";
import { Icon, IconType } from "../../../icons";
import { PiperDivider } from "../../PiperDivider";

export const ShelfSegmentOptionsButton: FunctionComponent<IShelfSegmentOptionsButtonProps> = ({
  className,
  style,
  buttonClassName,
  shelvings,
  setShelvings,
  shelving,
  shelf,
  shelfSegment,
  setIsLoading,
  onModifyShelving,
  onModifyShelf,
  onModifyShelfSegment,
  onFocus,
  onStats,
  onAssignAlbumsToShelfSegmentByFilter
}) => {
  const theme = useTheme();
  const [loading] = useState(false);
  const { menuProps, handleContextMenu, handleMenuClose } = useContextMenu();
  const { removeAllAlbumsFromShelfSegment } = useRemoveAllAlbumsFromShelfSegment();
  const [alertMessage, setAlertMessage] = useState("");
  const [alertIsOpen, setAlertIsOpen] = useState(false);
  const onRemoveAllAlbumsFromShelfSegment = async () => {
    setIsLoading(true);
    const { status, body } = await removeAllAlbumsFromShelfSegment({ uuid: shelfSegment.uuid });
    if (StatusCodesHelper.isSuccessful(status)) {
      toast.success(strings.generic_update_success);
      setShelvings(
        shelvings.map(auxShelving => {
          if (shelving.uuid == auxShelving.uuid) {
            auxShelving.shelves = auxShelving.shelves.map(auxShelf => {
              if (shelf.uuid == auxShelf.uuid) {
                auxShelf.segments = auxShelf.segments.map(auxSegment => {
                  if (shelfSegment.uuid == auxSegment.uuid) {
                    auxSegment.albums = [];
                  }
                  return auxSegment;
                });
              }
              return auxShelf;
            });
          }
          return auxShelving;
        })
      );
    } else {
      setAlertMessage(body.message);
      setAlertIsOpen(true);
    }
    setIsLoading(false);
  };
  return (
    <div className={className} style={style}>
      <ToolbarRawButton
        tooltip={strings.options}
        iconType={IconType.MoreVertical}
        onClick={handleContextMenu}
        loading={loading}
        buttonClassName={buttonClassName}
        iconClassName={styles.icon}
      />
      <Menu {...menuProps}>
        <div className={styles.tooltipDiv}>
          <p className={styles.tooltipName}>{shelfSegment?.name}</p>
          <p className={styles.tooltipSubtitle} style={{ color: theme.palette.primary.subtitle }}>
            {shelfSegment?.description}
          </p>
        </div>
        <PiperDivider style={{ marginTop: "6px", marginBottom: "4px" }} />
        <MenuItem
          key={"Focus"}
          onClick={() => {
            handleMenuClose();
            onFocus(shelf, shelfSegment);
          }}
          dense
        >
          <ListItemIcon>
            <Icon type={IconType.FrameInspect} />
          </ListItemIcon>
          {strings.shelf_segment_focus_option}
        </MenuItem>
        <MenuItem
          key={"Stats"}
          onClick={() => {
            handleMenuClose();
            onStats(shelfSegment);
          }}
          dense
        >
          <ListItemIcon>
            <Icon type={IconType.AreaChart} />
          </ListItemIcon>
          {strings.option_stats}
        </MenuItem>
        <MenuItem
          key={"EditShelving"}
          onClick={() => {
            handleMenuClose();
            onModifyShelving(shelving);
          }}
          dense
        >
          <ListItemIcon>
            <Icon type={IconType.EditSquare} />
          </ListItemIcon>
          {strings.shelving_edit_title}
        </MenuItem>
        <MenuItem
          key={"EditShelf"}
          onClick={() => {
            handleMenuClose();
            onModifyShelf(shelf);
          }}
          dense
        >
          <ListItemIcon>
            <Icon type={IconType.EditSquare} />
          </ListItemIcon>
          {strings.shelf_edit_title}
        </MenuItem>
        <MenuItem
          key={"EditShefSegment"}
          onClick={() => {
            handleMenuClose();
            onModifyShelfSegment(shelf, shelfSegment);
          }}
          dense
        >
          <ListItemIcon>
            <Icon type={IconType.EditSquare} />
          </ListItemIcon>
          {strings.shelf_segment_edit_title}
        </MenuItem>
        <MenuItem
          key={"AddFilteredAlbums"}
          onClick={() => {
            handleMenuClose();
            onAssignAlbumsToShelfSegmentByFilter(shelfSegment);
          }}
          dense
        >
          <ListItemIcon>
            <Icon type={IconType.PlaylistAdd} />
          </ListItemIcon>
          {strings.shelf_segment_assign_filtered_albums}
        </MenuItem>
        <MenuItem
          key={"RemoveAllAlbums"}
          onClick={() => {
            handleMenuClose();
            onRemoveAllAlbumsFromShelfSegment().then();
          }}
          sx={{ color: theme.palette.colors?.remove }}
          dense
        >
          <ListItemIcon sx={{ color: theme.palette.colors?.remove }}>
            <Icon type={IconType.DeleteForever} />
          </ListItemIcon>
          {strings.shelf_remove_all_albums}
        </MenuItem>
      </Menu>
      <AlertDialog message={alertMessage} open={alertIsOpen} setOpen={setAlertIsOpen} />
    </div>
  );
};

export interface IShelfSegmentOptionsButtonProps {
  className?: string;
  buttonClassName?: string;
  style?: CSSProperties;
  shelvings: IShelving[];
  setShelvings: (shelvings: IShelving[]) => void;
  shelving: IShelving;
  shelf: IShelf;
  shelfSegment: IShelfSegment;
  setIsLoading: (isLoading: boolean) => void;
  onModifyShelving: (shelving: IShelving) => void;
  onModifyShelf: (shelf: IShelf) => void;
  onModifyShelfSegment: (shelf: IShelf, segment: IShelfSegment) => void;
  onFocus: (shelf: IShelf, segment: IShelfSegment) => void;
  onStats: (segment: IShelfSegment) => void;
  onAssignAlbumsToShelfSegmentByFilter: (shelfSegment: IShelfSegment) => void;
}
